/**
 * 临时前端 Tool 获取请求。
 * 它独立于 Harness 业务 Tool 执行链路；保留现有 JSON-RPC 协议以供调试接入使用。
 */

export interface AssistantCallerTool {
  type: 'function'
  function: {
    name: string
    description: string
    parameters: Record<string, unknown>
  }
}

export interface McpToolDefinition {
  name: string
  description: string
  inputSchema: Record<string, unknown>
  outputSchema?: unknown
}

const HARNESS_TOOL_NAME = /^[A-Za-z0-9_-]{1,64}$/
const MAX_HARNESS_TOOL_DESCRIPTION_LENGTH = 4096

/**
 * 将 MCP 的 inputSchema 转为 Harness 调用方 Tool 的 parameters。
 * outputSchema 描述执行结果，不属于 Harness 请求的 Tool 定义。
 */
export function mapMcpToolsToAssistantTools(
  tools: McpToolDefinition[]
): AssistantCallerTool[] {
  const names = new Set<string>()
  return tools.map((tool) => {
    const name = tool.name.trim()
    const description = tool.description.trim()
    const properties = tool.inputSchema.properties
    if (!HARNESS_TOOL_NAME.test(name)) {
      throw new Error(`MCP Tool 名称不符合 Harness 规则：${tool.name}`)
    }
    if (
      !description ||
      description.length > MAX_HARNESS_TOOL_DESCRIPTION_LENGTH
    ) {
      throw new Error(`MCP Tool 描述不符合 Harness 规则：${name}`)
    }
    if (
      tool.inputSchema.type !== 'object' ||
      !properties ||
      typeof properties !== 'object' ||
      Array.isArray(properties)
    ) {
      throw new Error(`MCP Tool inputSchema 无效：${name}`)
    }
    if (names.has(name)) {
      throw new Error(`MCP Tool 名称重复：${name}`)
    }
    names.add(name)
    return {
      type: 'function',
      function: {
        name,
        description,
        parameters: tool.inputSchema,
      },
    }
  })
}

export async function fetchGetTools(): Promise<AssistantCallerTool[]> {
  const res = await globalThis.fetch('https://test-api.eggrj.com/guigui', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      token: sessionStorage.getItem('token') || '',
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'tools/list',
    }),
  })
  const data = (await res.json()) as {
    result?: { tools?: unknown }
  }
  const tools = data.result?.tools
  if (!Array.isArray(tools)) {
    throw new Error('MCP 服务未返回有效 Tool 列表')
  }
  return mapMcpToolsToAssistantTools(tools as McpToolDefinition[])
}

export interface MaterialExportRecord {
  id: number
  name: string
  create_time: string
  file_type: string
  layout_batch: string
  process_setting_id: number
  production_line: string
  order_codes: string
  buyer_address?: string | null
  customer_name?: string | null
  download_url?: string
}

export interface MaterialExportRecordsResult {
  total: number
  returned: number
  records: MaterialExportRecord[]
  note: string
  msg?: unknown
}

export interface OrderListRoom {
  id: number
  name: string
  create_time: string
}

export interface OrderListItem {
  id: number
  order_code: string
  buyer_address: string
  customer_name: string
  create_time: string
  rooms: OrderListRoom[]
}

export interface OrderListResult {
  total: number
  orders: OrderListItem[]
}

interface McpToolCallResponse {
  result?: {
    content?: Array<{ type: string; text: string }>
    structuredContent?: MaterialExportRecordsResult | OrderListResult
    isError?: boolean
  }
}

export async function fetchProductionRecords(
  name: string,
  arguments_: Record<string, unknown>
): Promise<MaterialExportRecordsResult> {
  const res = await globalThis.fetch(
    process.env.VUE_APP_ASSISTANT_API_TOOL_URL!,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        token: sessionStorage.getItem('token') || '',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 2,
        method: 'tools/call',
        params: { name, arguments: arguments_ },
      }),
    }
  )
  const data = (await res.json()) as McpToolCallResponse
  if (data.result?.isError || !data.result?.structuredContent) {
    throw new Error(
      data.result?.content?.map((item) => item.text).join('') ||
        '生产文件记录查询失败'
    )
  }
  return data.result.structuredContent as MaterialExportRecordsResult
}

export async function fetchOrderList(
  name: string,
  arguments_: Record<string, unknown>
): Promise<OrderListResult> {
  const res = await globalThis.fetch(
    process.env.VUE_APP_ASSISTANT_API_TOOL_URL!,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        token: sessionStorage.getItem('token') || '',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 3,
        method: 'tools/call',
        params: { name, arguments: arguments_ },
      }),
    }
  )
  const data = (await res.json()) as McpToolCallResponse
  if (data.result?.isError || !data.result?.structuredContent) {
    throw new Error(
      data.result?.content?.map((item) => item.text).join('') || '订单查询失败'
    )
  }
  return data.result.structuredContent as OrderListResult
}
